import {IoStsMap, IoStsVariant, IO_STS_VOL_DOWN} from '@/common/constants';
import {speech, beepSound} from '@/common/utils';

// 유량감소 판정 창. 최근 VOL_DOWN_WIN 회 변화 중 VOL_DOWN_CNT 회 이상
// 환산재고(ovc)가 하락하면 표시한다.
// 두 시점만 비교하면 센서가 한 번 튀는 것에 걸리므로 추세로 본다.
const VOL_DOWN_WIN = 10;
const VOL_DOWN_CNT = 5;

/**
 * 탱크 입출고 상태 표시 공통 로직
 *
 * Tank2D / Tank3D / Tank3DV2 가 각자 setInoutSts() / setTankStatus() 를 들고 있었고
 * 처리하는 상태, 라벨, 진척률 산식, 음성안내가 조금씩 달랐다.
 * 같은 화면 요소인데 컴포넌트마다 다르게 보이던 것을 하나로 맞춘다.
 *
 * 통일하면서 정한 기준
 *  - 라벨은 IoStsMap 을 쓴다. Tank3D/Tank3DV2 에 섞여 있던 '입고중','저장중','미확인'
 *    같은 하드코딩을 없앤다. (Tank2D 가 이미 IoStsMap 만 쓰고 있었고 사용처가 가장 많다)
 *  - 상태 필드명은 statusMsg 로 맞춘다. (Tank3DV2 만 tankStatus 였다)
 *  - R/D(급유·배유)까지 처리한다. Tank3D/Tank3DV2 에는 분기가 없어 상태가 갱신되지 않았다.
 *  - 진척률은 분모가 0 이면 0 으로 둔다. 목표량 없이 자동 시작된 입고에서
 *    Infinity/NaN 이 나오던 문제를 막는다.
 *  - 음성안내는 탱크명을 포함한다. Tank3DV2 의 '경고! 입고량 확인.' 처럼
 *    어느 탱크인지 알 수 없는 문구를 없앤다.
 *
 * 사용하는 컴포넌트는 tank, pk 를 갖고 있어야 하며, tnm(탱크명)과
 * alarmCfg(경보설정)는 있으면 쓰고 없으면 건너뛴다.
 */
export default {
  data(){
    return {
      statusMsg: 'wait...',
      statusVariant: 'dark',
      targetVol: 0,   // 목표량 (liter)
      diffOvm: 0,     // 시작 대비 누적 변화량 - 실측(gross), liter, 표시용 양수
      diffOvc: 0,     // 시작 대비 누적 변화량 - 온도환산(net), liter, 표시용 양수
      volPercent: 0,  // 진척률 (%)
      ovcHist: [],    // 최근 환산재고(ovc) 이력. 유량감소 판정에 쓴다.
    };
  },
  methods: {
    /**
     * 숫자가 아닌 값을 0 으로 떨어뜨린다.
     * 서버 페이로드에 따라 일부 필드가 빠져 오는 경우가 있어 표시가 비지 않게 한다.
     */
    numOrZero(v){
      return Number.isFinite(v) ? v : 0;
    },

    /**
     * 0 나눗셈을 막은 백분율
     */
    ratioPercent(value, base){
      if( !base || !Number.isFinite(base) || !Number.isFinite(value) ) return 0;
      return Math.round(value / base * 100);
    },

    /**
     * 목표 도달 시 알림. 경보설정에서 tts 가 꺼져 있으면 아무것도 하지 않는다.
     */
    notifyInoutDone(word){
      if( !(this.targetVol > 0 && this.volPercent >= 100) ) return;
      if( !this.alarmCfg?.config?.console?.tts ) return;

      const name = this.tnm || this.tank?.name || '';
      beepSound('primary');
      speech(`${name} ${word} 확인.`);
    },

    /**
     * 입출고 작업 없이 재고가 줄어드는지 보고 상태 뱃지를 유량감소로 바꾼다.
     *
     * 상태를 정한 뒤(applyInoutSts/applyTankSts) 마지막에 불러야 한다. 뱃지를 덮어쓰기 때문이다.
     *
     * 입출고 중에는 정상적인 재고 변화이므로 판정하지 않는다.
     *
     * 최근 VOL_DOWN_WIN 회 변화 중 VOL_DOWN_CNT 회 이상 하락했을 때만 표시한다.
     * 두 시점만 비교하면 센서가 한 번 튀는 것에 그대로 걸리므로 추세로 본다.
     *
     * 유위(ohr)나 측정재고(ovm)가 아니라 온도환산재고(ovc)로 본다.
     * 유종이 식으면 유위와 ovm 은 줄지만 ovc 는 거의 변하지 않아 열팽창이 걸러진다.
     *
     * IO_STS_VOL_DOWN 은 화면 표시 전용 코드다. 서버의 tank.sts.io 값이 아니다.
     *
     * @param tp 현재 패킷
     */
    checkVolumeDown(tp){
      if( !tp || !Number.isFinite(tp.ovc) ) return;

      this.ovcHist.push(tp.ovc);
      // 변화 VOL_DOWN_WIN 회를 보려면 값이 VOL_DOWN_WIN+1 개 필요하다
      const need = VOL_DOWN_WIN + 1;
      if( this.ovcHist.length > need ) this.ovcHist.splice(0, this.ovcHist.length - need);

      if( this.ovcHist.length < need ) return;
      // 입출고 작업 중이면 그 상태 표시를 유지한다
      if( this.inout && this.inout.sts && this.inout.sts !== 'N' ) return;

      let downCount = 0;
      for(let i = 1; i < this.ovcHist.length; i++){
        if( this.ovcHist[i] < this.ovcHist[i - 1] ) downCount++;
      }

      if( downCount >= VOL_DOWN_CNT ){
        this.statusMsg = IoStsMap[IO_STS_VOL_DOWN];
        this.statusVariant = IoStsVariant[IO_STS_VOL_DOWN]; // info 가 아니므로 뱃지가 깜빡인다
      }
    },

    /**
     * 패킷의 탱크상태(tp.sts)로 라벨과 색만 갱신한다.
     * @param sts {Object} {io}
     */
    applyTankSts(sts){
      if( !sts ) return;
      const io = sts.io;
      this.statusMsg = IoStsMap[io] !== undefined ? IoStsMap[io] : IoStsMap[''];
      this.statusVariant = IoStsVariant[io] || 'dark';
    },

    /**
     * 입출고 정보로 라벨·색·진척률을 갱신한다.
     * @param inout {Object} inout_log
     */
    applyInoutSts(inout){
      if( !inout ) return;
      this.inout = inout;

      const sts = inout.sts;
      this.statusMsg = IoStsMap[sts] !== undefined ? IoStsMap[sts] : IoStsMap[''];
      this.statusVariant = IoStsVariant[sts] || 'dark';

      switch(sts){
        case 'R': // 급유차 급유 (탱크에서 나감)
          this.targetVol = Math.abs(inout.outOvm);
          this.diffOvm = Math.abs(this.numOrZero(inout.outOvm));
          this.diffOvc = Math.abs(this.numOrZero(inout.outOvc));
          this.volPercent = this.ratioPercent(this.diffOvm, this.pk?.ofm);
          break;

        case 'O': // 출고
          this.targetVol = inout.targetVol;
          this.diffOvm = Math.abs(this.numOrZero(inout.outOvm));
          this.diffOvc = Math.abs(this.numOrZero(inout.outOvc));
          this.volPercent = this.ratioPercent(inout.outOvm, this.targetVol);
          this.notifyInoutDone('출고량');
          break;

        case 'D': // 급유차 배유 (탱크로 들어옴)
          this.targetVol = Math.abs(inout.inOvm);
          this.diffOvm = Math.abs(this.numOrZero(inout.inOvm));
          this.diffOvc = Math.abs(this.numOrZero(inout.inOvc));
          this.volPercent = this.ratioPercent(this.diffOvm, this.tank?.tankVolume);
          break;

        case 'I': // 입고
          this.targetVol = inout.targetVol;
          this.diffOvm = this.numOrZero(inout.inOvm);
          this.diffOvc = this.numOrZero(inout.inOvc);
          this.volPercent = this.ratioPercent(inout.inOvm, this.targetVol);
          this.notifyInoutDone('입고량');
          break;

        case 'N': // 저장 (작업 없음)
          this.targetVol = 0;
          this.diffOvm = 0;
          this.diffOvc = 0;
          this.volPercent = 0;
          break;

        default:
          this.statusVariant = 'dark';
          break;
      }
    },
  },
};